import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SideMenu from './SideMenu';
import { dashboardService, subscriptionService, lessonService } from '../services/api';
import './Home.css';
import { useTheme } from '../ThemeContext';

// Import icons
import { BookOpen, Target, Clock, TrendingUp, Play, ChevronRight, Brain, Calendar, Zap } from 'lucide-react';

// Custom hook for responsive icon sizes
const useResponsiveIconSize = () => {
  const [iconSize, setIconSize] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 480 ? 16 : window.innerWidth < 768 ? 18 : 20;
    }
    return 20;
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 480) {
        setIconSize(16);
      } else if (width < 768) {
        setIconSize(18);
      } else {
        setIconSize(20);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return iconSize;
};

// Enhanced Stats Panel Component
const StatsPanel = ({ stats, theme }) => {
  const iconSize = useResponsiveIconSize();
  // Use completion percentage from backend, fallback to local calculation if not available
  const completionPercentage = stats.completionPercentage || 
    (stats.totalLessons > 0 ? (stats.completedLessons / stats.totalLessons) * 100 : 0);
  
  return (
    <div className={`home-panel stats-panel ${theme}`}>
      <div className="panel-header">
        <div className="panel-title">
          <TrendingUp size={iconSize} />
          <h2>Learning Progress</h2>
        </div>
      </div>
      <div className="stats-content">
        <div className="progress-circle-container">
          <div className="progress-circle">
            <svg viewBox="0 0 100 100" className="progress-svg">
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                className="progress-bg"
                fill="none" 
                stroke="rgba(255,255,255,0.1)" 
                strokeWidth="8"
              />
              <circle 
                cx="50" 
                cy="50" 
                r="45" 
                className="progress-fill"
                fill="none" 
                stroke="var(--dark-primary)" 
                strokeWidth="8"
                strokeDasharray={`${completionPercentage * 2.83} 283`}
                strokeLinecap="round"
                transform="rotate(-90 50 50)"
              />
            </svg>
            <div className="progress-text">
              <span className="progress-percentage">{Math.round(completionPercentage)}%</span>
              <span className="progress-label">Complete</span>
            </div>
          </div>
        </div>
        
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon">
              <BookOpen size={Math.max(14, iconSize - 4)} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.completedLessons}</div>
              <div className="stat-label">Lessons Done</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon">
              <Target size={Math.max(14, iconSize - 4)} />
            </div>
            <div className="stat-info">
              <div className="stat-value">{stats.completedRoadmaps}</div>
              <div className="stat-label">Topics Done</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Current Lesson Panel Component
const CurrentLessonPanel = ({ currentLesson, theme }) => {
  const navigate = useNavigate();
  const iconSize = useResponsiveIconSize();

  if (!currentLesson) {
    return (
      <div className={`home-panel current-lesson-panel ${theme}`}>
        <div className="panel-header">
          <div className="panel-title">
            <Play size={iconSize} />
            <h2>Continue Learning</h2>
          </div>
        </div>
        <div className="current-lesson-content">
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <p>No active lesson found</p>
            <button 
              className="start-learning-btn"
              onClick={() => navigate('/learning-plan')}
            >
              Start Learning
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleStartLesson = async () => {
    const lessonData = await lessonService.getCurrentLesson();
    if (lessonData && lessonData.id) {
      navigate(`/lessons/${lessonData.Id || lessonData.id}`);
    }
  };

  return (
    <div className={`home-panel current-lesson-panel ${theme}`}>
      <div className="panel-header">
        <div className="panel-title">
          <Play size={iconSize} />
          <h2>Current Lesson</h2>
        </div>
      </div>
      <div className="current-lesson-content">
        <div className="lesson-preview">
          <div className="lesson-info">
            <h3 className="home-lesson-title">{currentLesson.Title || currentLesson.title}</h3>
            <p className="lesson-roadmap">
              {currentLesson.Roadmap?.Name || currentLesson.roadmap?.name || 'Current Topic'}
            </p>
            <div className="lesson-meta">
              <span className="lesson-duration">
                <Clock size={Math.max(12, iconSize - 6)} />
                ~30 min
              </span>
            </div>
          </div>
          <button 
            className="continue-lesson-btn"
            onClick={handleStartLesson}
          >
            <Play size={Math.max(14, iconSize - 4)} />
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

// Quick Actions Panel Component
const QuickActionsPanel = ({ theme }) => {
  const navigate = useNavigate();
  const iconSize = useResponsiveIconSize();

  const actions = [
    {
      icon: <Target size={iconSize} />,
      title: 'Set New Goal',
      description: 'Define your learning objectives',
      action: () => navigate('/goals'),
      color: 'var(--dark-primary)'
    },
    {
      icon: <BookOpen size={iconSize} />,
      title: 'Browse Topics',
      description: 'Explore available subjects',
      action: () => navigate('/learning-plan'),
      color: '#10B981'
    },
    {
      icon: <Brain size={iconSize} />,
      title: 'Knowledge Base',
      description: 'Manage your notes and insights',
      action: () => navigate('/knowledge-graph'),
      color: '#F59E0B'
    }
  ];

  return (
    <div className={`home-panel quick-actions-panel ${theme}`}>
      <div className="panel-header">
        <div className="panel-title">
          <Zap size={iconSize} />
          <h2>Quick Actions</h2>
        </div>
      </div>
      <div className="quick-actions-content">
        {actions.map((action, index) => (
          <div 
            key={index} 
            className="action-card"
            onClick={action.action}
            style={{ '--action-color': action.color }}
          >
            <div className="action-icon" style={{ color: action.color }}>
              {action.icon}
            </div>
            <div className="action-info">
              <h4 className="action-title">{action.title}</h4>
              <p className="action-description">{action.description}</p>
            </div>
            <ChevronRight size={Math.max(14, iconSize - 4)} className="action-arrow" />
          </div>
        ))}
      </div>
    </div>
  );
};

// Enhanced Active Goal Panel Component
const ActiveGoalPanel = ({ goal, theme }) => {
  const navigate = useNavigate();
  const iconSize = useResponsiveIconSize();

  if (!goal) {
    return (
      <div className={`home-panel home-active-goal-panel ${theme}`}>
        <div className="panel-header">
          <div className="panel-title">
            <Target size={iconSize} />
            <h2>Active Goal</h2>
          </div>
          <button className="view-all-link" onClick={() => navigate('/goals')}>
            Manage Goals
          </button>
        </div>
        <div className="home-active-goal-content">
          <div className="empty-state">
            <div className="empty-icon">🎯</div>
            <p>You don't have an active goal.</p>
            <button 
              className="create-goal-btn"
              onClick={() => navigate('/goals')}
            >
              Create a Goal
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getMetadata = (metadataStr) => {
    if (!metadataStr) return { level: "Not specified" };
    try {
      return JSON.parse(metadataStr);
    } catch (err) {
      return { level: "Not specified" };
    }
  };

  const metadata = getMetadata(goal.metadata);
  // Use goal-specific completion percentage, fallback to 0 if not available
  const progress = goal.goalProgress?.completionPercentage || goal.GoalProgress?.CompletionPercentage || 0;
  const goalCompletedLessons = goal.goalProgress?.completedLessons || goal.GoalProgress?.CompletedLessons || 0;
  const goalTotalLessons = goal.goalProgress?.totalLessons || goal.GoalProgress?.TotalLessons || 0;

  return (
    <div className={`home-panel home-active-goal-panel ${theme}`}>
      <div className="panel-header">
        <div className="panel-title">
          <Target size={iconSize} />
          <h2>Active Goal</h2>
        </div>
        <button className="view-all-link" onClick={() => navigate('/goals')}>
          Manage Goals
        </button>
      </div>
      <div className="home-active-goal-content">
        <div className="home-goal-card-modern">
          {goal.imageUrl && (
            <div className="home-goal-image-container">
              <img 
                src={goal.imageUrl.startsWith('http') 
                  ? goal.imageUrl 
                  : `https://nd69zr.realhost-free.net${goal.imageUrl}`
                } 
                alt={goal.description}
                className="home-goal-image"
                onError={(e) => {
                  console.error("Goal image failed to load:", goal.imageUrl);
                  e.target.onerror = null;
                  e.target.src = `https://nd69zr.realhost-free.net/goal-images/default-goal.svg`;
                }}
              />
            </div>
          )}
          
          <div className="home-goal-content-wrapper">
            <div className="home-goal-header">
              <h3 className="home-goal-title">{goal.description}</h3>
            </div>
            
            <div className="home-goal-progress">
              <div className="home-progress-bar">
                <div 
                  className="home-progress-fill" 
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="home-progress-info-wrapper">
                <div className="home-progress-info">
                  <div className="home-progress-main-line">
                    <div className="home-progress-text-group">
                      <span className="home-progress-percentage">{progress}% complete</span>
                      {goal && (
                        <span className="home-progress-details">
                          {goalCompletedLessons} of {goalTotalLessons} lessons completed
                        </span>
                      )}
                    </div>
                    <div className="home-goal-actions">
                      <button 
                        className="home-primary-action-btn"
                        onClick={() => navigate(`/learning-plan`)}
                      >
                        <Play size={Math.max(12, iconSize - 8)} />
                        Continue Learning
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="skeleton-container">
      <div className="skeleton stats-skeleton"></div>
      <div className="skeleton current-lesson-skeleton"></div>
      <div className="skeleton quick-actions-skeleton"></div>
      <div className="skeleton active-goal-skeleton"></div>
    </div>
  );
};


// Main Home Component
const Home = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeGoal, setActiveGoal] = useState(null);
  const [stats, setStats] = useState({
    completedLessons: 0,
    totalLessons: 0,
    completedRoadmaps: 0,
    totalRoadmaps: 0
  });
  const { theme } = useTheme();
  const homeContainerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const dashboardData = await dashboardService.getDashboardData();
        
        if (dashboardData) {
          // Handle active goal
          const activeGoalData = dashboardData.ActiveGoal || dashboardData.activeGoal;
          setActiveGoal(activeGoalData);
          
          // Log image URLs for debugging (similar to GoalsPage)
          console.log("Using hardcoded API URL: https://nd69zr.realhost-free.net");
          if (activeGoalData?.imageUrl) {
            console.log("Active goal image URL:", activeGoalData.imageUrl);
            console.log("Constructed URL:", activeGoalData.imageUrl.startsWith('http') 
              ? activeGoalData.imageUrl 
              : `https://nd69zr.realhost-free.net${activeGoalData.imageUrl}`);
          }
          
          // Handle current lesson
          const currentLessonData = dashboardData.CurrentLesson || dashboardData.currentLesson;
          setCurrentLesson(currentLessonData);
          
          // Handle stats
          const statsData = dashboardData.Stats || dashboardData.stats;
          if (statsData) {
            setStats({
              completedLessons: statsData.CompletedLessons || statsData.completedLessons || 0,
              totalLessons: statsData.TotalLessons || statsData.totalLessons || 0,
              completedRoadmaps: statsData.CompletedRoadmaps || statsData.completedRoadmaps || 0,
              totalRoadmaps: statsData.TotalRoadmaps || statsData.totalRoadmaps || 0,
              completionPercentage: statsData.CompletionPercentage || statsData.completionPercentage || 0
            });
          }
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    // Listen for sidebar toggle events
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.collapsed);
    };
    
    document.addEventListener('sidebar-toggle', handleSidebarToggle);
    
    return () => {
      document.removeEventListener('sidebar-toggle', handleSidebarToggle);
    };
  }, []);

  return (
    <div className="app-layout">
      <SideMenu />
      <div className={`home-main-content ${sidebarCollapsed ? 'sidebar-collapsed' : ''} ${theme}`}>
        <div ref={homeContainerRef} className={`home-container`}>
          {/* <div className="home-header">
            <h1>Welcome back! 👋</h1>
            <p>Ready to continue your learning journey?</p>
          </div> */}
          
          {isLoading ? (
            <SkeletonLoader />
          ) : (
            <div className="home-grid">
              <StatsPanel stats={stats} theme={theme} />
              <CurrentLessonPanel currentLesson={currentLesson} theme={theme} />
              <QuickActionsPanel theme={theme} />
              <ActiveGoalPanel goal={activeGoal} theme={theme} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;